import { Location } from "gen/company/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { ReactNode, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import { Chip } from "shared-web/components/base/informational/Chip"

import { useListLocationsForCompanyQuery } from "features/api/company"

import useLocalSearchPopoverMultiSelect, {
  UseLocalSearchPopoverMultiSelectProps,
  UseLocalSearchPopoverSelectRes,
} from "./useLocalSearchPopoverMultiSelect"

type UseLocationsPopoverMultiSelectProps = Omit<
  UseLocalSearchPopoverMultiSelectProps<Location>,
  | "items"
  | "keyExtractor"
  | "labelExtractor"
  | "contentExtractor"
  | "onChangeQuery"
>

export interface UseLocationsPopoverMultiSelectRes
  extends UseLocalSearchPopoverSelectRes<Location> {
  selectedIds: PBUUID[]
}

function useLocationsPopoverMultiSelect(
  props: UseLocationsPopoverMultiSelectProps = {}
): UseLocationsPopoverMultiSelectRes {
  const { data } = useListLocationsForCompanyQuery({ filterActive: true })

  const sortedLocations = useMemo(() => {
    return [...(data?.locations ?? [])].sort((a, b) =>
      a.prefix.localeCompare(b.prefix)
    )
  }, [data?.locations])

  const res = useLocalSearchPopoverMultiSelect({
    items: sortedLocations,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    ...props,
  })

  const selectedIds = useMemo(() => {
    if (!res.selected) return []
    return res.selected
      .map((location) => location.id)
      .filter((id): id is PBUUID => id !== undefined)
  }, [res.selected])

  return {
    ...res,
    selectedIds,
  }
}

function keyExtractor(location: Location): string {
  return UUID.idString(location.id)
}

function labelExtractor(location: Location): string {
  return location.name
}

function accessoryExtractor(location: Location): ReactNode {
  return <Chip color="gray" text={location.prefix} />
}

export default useLocationsPopoverMultiSelect
